import moment from 'moment';

import { DisplayType } from '@models/module/fields/enums';
import {
  AddressModel,
  AttachemntFieldValueType,
  AttachmentModel,
  BaseFieldValueType,
  MimeTypesEnum,
} from '@models/module/fields/main';
import { Currency } from '@models/system';
import {
  CipoAddressConfig,
  CipoAttachmentConfig,
  CipoCheckboxConfig,
  CipoDateTimeConfig,
  CipoFieldConfig,
  CipoFieldTypes,
  CipoFieldValue,
  CipoListConfig,
  CipoNumberConfig,
  CipoTextConfig,
  CipoTextViewConfig,
} from '@shared/components/fields/common';
import { ACCENT_BG_COLOR, WARN_BG_COLOR } from '@shared/consts';

export type DemoFieldData<T = CipoFieldConfig, V = CipoFieldValue> = T & {
  name: string;
  defaultValue?: V;
};

export const demoFields: DemoFieldData[] = [
  {
    type: CipoFieldTypes.Text,
    name: '1firstName',
    label: 'First Name',
    hint: 'Enter your first name',
    defaultValue: 'John',
    fieldDescription: 'This is your first name',
  },
  {
    type: CipoFieldTypes.Text,
    name: '2lastName',
    label: 'Last Name',
    hint: 'Enter your last name',
    defaultValue: 'Doe',
    multiline: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '3message',
    label: 'Message',
    hint: 'Enter your message',
    defaultValue: ['Hello', 'World!'],
    multiple: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '4description',
    label: 'Description',
    hint: 'This is a multiline field',
    defaultValue: 'Enter your description \nThis is a multiline field',
    multiline: true,
  },
  {
    type: CipoFieldTypes.Number,
    name: '8age',
    label: 'Age',
    hint: 'Enter your age (without thousand separator)',
    defaultValue: 25,
    required: true,
  },
  {
    type: CipoFieldTypes.Number,
    name: '6price',
    label: 'Price',
    hint: 'Enter the price, 2 decimals allowed',
    defaultValue: 1003232.12,
    decimals: 2,
    currency: { symbol: '$' } as Currency,
    showThousandSeparator: true,
  },
  {
    type: CipoFieldTypes.Checkbox,
    label: 'Required checkbox',
    name: '7requiredCheckbox',
    defaultValue: false,
    required: true,
    fieldDescription: 'This is a required checkbox',
    hint: 'checkbox hint',
  },
  {
    type: CipoFieldTypes.Number,
    name: '9quantity',
    label: 'Quantity',
    hint: 'Enter the quantity, 3 decimals allowed',
    defaultValue: [10],
    multiple: true,
    required: true,
    showThousandSeparator: true,
    decimals: 3,
  },
  {
    type: CipoFieldTypes.Listbox,
    name: '5options',
    label: 'Radio Options',
    defaultValue: [],
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value: 'Option 3',
      },
    ],
    multiple: true,
    required: true,
    columns: 2,
    fieldDescription: 'Select one or more options',
    hint: 'Select one or more options',
  },
];

export const DemoTextFields: DemoFieldData<CipoTextConfig, string | string[]>[] = [
  {
    type: CipoFieldTypes.Text,
    name: '11basic',
    label: 'Basic Text Field',
  },
  {
    type: CipoFieldTypes.Text,
    name: '12firstName',
    label: 'First Name',
    hint: 'Field with max 20 characters',
    maxLength: 20,
    readonly: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '13lastName',
    label: 'Last Name',
    hint: 'Field with hint',
    defaultValue: 'John',
    required: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '14field',
    label: 'Field with description',
    fieldDescription: 'This is a field description',
  },
  {
    type: CipoFieldTypes.Text,
    name: '15prefix',
    label: 'Street Address',
    readonly: true,
    multiple: true,
    hint: 'Field with prefix and suffix icon',
    defaultValue: ['123 Main St', 'Apt 4B', 'New York, NY 10001', 'USA'],
    prefix: 'Str: ',
    suffixIcon: 'home',
  },
  {
    type: CipoFieldTypes.Text,
    name: '16suffix',
    label: 'Street Address 2',
    hint: 'Field with prefix icon and suffix',
    prefixIcon: 'home',
    suffix: 'st',
  },
  {
    type: CipoFieldTypes.Text,
    name: '17multiple',
    label: 'Multiple values',
    hint: 'Multiple inputs, required',
    defaultValue: ['First Value', 'Second Value'],
    multiple: true,
    required: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '18textdescription',
    label: 'Text Description',
    hint: 'This is a multiline field',
    defaultValue: 'Enter your description \nThis is a multiline field',
    multiline: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '19textdescription',
    label: 'Text Description',
    hint: 'This is a multiline field, required',
    defaultValue: 'Enter your description \nThis is a multiline field',
    required: true,
    multiline: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '20textdescription',
    label: 'Text Description readonly',
    hint: 'This is a multiline field',
    defaultValue: 'Enter your description \nThis is a multiline field',
    multiline: true,
    readonly: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '21multiple',
    label: 'Multiple values',
    hint: 'Multiple inputs, readonly required',
    defaultValue: ['First Value', 'Second Value'],
    multiple: true,
    readonly: true,
    required: true,
  },
  {
    type: CipoFieldTypes.Text,
    name: '22address',
    label: 'Street Address readonly',
    hint: 'Field with prefix and suffix icon',
    defaultValue: '123 Main St',
    prefix: 'Str: ',
    suffixIcon: 'home',
    readonly: true,
  },
];

export const DemoNumberFields: DemoFieldData<CipoNumberConfig, number | number[]>[] = [
  {
    type: CipoFieldTypes.Number,
    name: '10basic',
    label: 'Basic Number Field',
  },
  {
    type: CipoFieldTypes.Number,
    name: '11age',
    label: 'Age',
    min: 18,
    max: 100,
    hint: 'Enter your age (integer)',
    fieldDescription: "The age is required, and doesn't have thousand separator",
    required: true,
  },
  {
    type: CipoFieldTypes.Number,
    name: '12separator',
    label: 'Number with thousand separator',
    hint: 'This is a simple number field with thousand separator',
    defaultValue: 2325,
    showThousandSeparator: true,
  },
  {
    type: CipoFieldTypes.Number,
    name: '13decimal',
    label: 'Number with decimals',
    hint: 'This is a simple decimal field without thousand separator',
    defaultValue: 2325.32,
    decimals: 2,
  },
  {
    type: CipoFieldTypes.Number,
    name: '13price',
    label: 'Price',
    hint: 'Field with prefix and suffix icon',
    defaultValue: 1003232.12,
    decimals: 2,
    prefix: '$',
    suffixIcon: 'home',
    showThousandSeparator: true,
  },
  {
    type: CipoFieldTypes.Number,
    name: '14quantity',
    label: 'Quantity',
    hint: 'Enter the quantity, 1 decimals allowed',
    defaultValue: [10],
    multiple: true,
    required: true,
    showThousandSeparator: true,
    decimals: 1,
  },
  {
    type: CipoFieldTypes.Number,
    name: '15price',
    label: 'Price readonly',
    hint: 'Field with prefix and suffix icon',
    defaultValue: 1003232.12,
    decimals: 2,
    readonly: true,
    showThousandSeparator: true,
    suffixIcon: 'money',
    currency: { symbol: 'RON', noSpace: false, leftSide: false } as Currency,
  },
  {
    type: CipoFieldTypes.Number,
    name: '16percent',
    label: 'Percent readonly',
    hint: 'Field with multiple values and suffix',
    defaultValue: [10.1, 22.4, 12221],
    decimals: 1,
    multiple: true,
    readonly: true,
    suffix: '%',
    suffixIcon: 'money',
    showThousandSeparator: true,
  },
  {
    type: CipoFieldTypes.Number,
    name: '17minMax',
    label: 'Min Max',
    hint: 'Field with min (10) and max value (100)',
    min: 10,
    max: 100,
  },
  {
    type: CipoFieldTypes.Number,
    name: '18minMax',
    label: 'Min Max multiple',
    hint: 'Field with min (10) and max (100) multiple values',
    multiple: true,
    min: 10,
    max: 100,
  },
  {
    type: CipoFieldTypes.Number,
    name: '19priceMultiple',
    label: 'Price multiple',
    hint: 'Field with prefix and suffix icon',
    defaultValue: [1003232.12, 1234.56],
    multiple: true,
    decimals: 2,
    showThousandSeparator: true,
    currency: { symbol: '$', noSpace: false, leftSide: true } as Currency,
  },
];

export const DemoDateTimeFields: DemoFieldData<CipoDateTimeConfig, string | string[]>[] = [
  {
    type: CipoFieldTypes.DateTime,
    name: '1dateTime',
    label: 'Basic Date Time',
    hint: 'Select a date and time',
    pickerType: 'dateTime',
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '20date',
    label: 'Date only',
    hint: 'Select a date',
    pickerType: 'date',
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '21date',
    label: 'Date only',
    hint: 'Select a date until today',
    pickerType: 'date',
    fieldDescription: 'You can select any date from past. MaxDate is set to today',
    maxDate: moment().toISOString(),
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '22date',
    label: 'Date only',
    defaultValue: moment().add(2, 'days').toISOString(),
    hint: 'Select a date from today',
    pickerType: 'date',
    fieldDescription: 'You can select any date from future. MinDate is set to today',
    minDate: moment().toISOString(),
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '3time',
    label: 'Time only',
    hint: 'Select a time',
    pickerType: 'time',
    defaultValue: '2025-12-05T08:30:00',
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '40range',
    label: 'Date Time Range',
    hint: 'Select a date and time range',
    pickerType: 'dateTime',
    selectMode: 'range',
    required: true,
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '41range',
    label: 'Date Range',
    hint: 'Select a date range. Max range is 3 day',
    pickerType: 'date',
    selectMode: 'range',
    maxDate: moment().toISOString(),
    maxRange: 3,
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '42range',
    label: 'Date Range',
    hint: 'Select a date range until today. Max range is 3 day',
    fieldDescription: 'You can select a date range with max 3 days. The last date is today',
    pickerType: 'date',
    selectMode: 'range',
    maxRange: 3,
    maxDate: moment().toISOString(),
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '5readonly',
    label: 'Date Time readonly',
    pickerType: 'dateTime',
    readonly: true,
    defaultValue: '2021-01-01T00:00:00.000Z',
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '6readonly',
    label: 'Date Time Range readonly',
    pickerType: 'dateTime',
    selectMode: 'range',
    readonly: true,
    defaultValue: ['2021-01-01T00:00:00.000Z', '2021-01-02T00:00:00.000Z'],
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '7readonly',
    label: 'Time readonly',
    pickerType: 'time',
    readonly: true,
    defaultValue: '2025-12-05T08:30:00',
  },
  {
    type: CipoFieldTypes.DateTime,
    name: '8readonly',
    label: 'Time range readonly',
    selectMode: 'range',
    pickerType: 'time',
    readonly: true,
    defaultValue: [moment().toISOString(), moment().add(400, 'minute').toISOString()],
  },
];

export const DemoListboxFields: DemoFieldData<CipoListConfig, string | string[]>[] = [
  {
    type: CipoFieldTypes.Listbox,
    name: '1options',
    label: 'Single listbox (required)',
    defaultValue: [],
    dataSourceInfo: {
      id: -25,
    },
    required: true,
    hint: 'Select one option',
  },
  {
    type: CipoFieldTypes.Listbox,
    name: '2options',
    label: 'Multiple listbox',
    defaultValue: ['option1', 'option2'],
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value:
          'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
      },
    ],
    columns: 2,
    multiple: true,
    fieldDescription: 'Options in 2 columns',
    hint: 'Select one or more options',
  },
  {
    type: CipoFieldTypes.Listbox,
    name: '3options',
    label: 'Multiple listbox readonly',
    defaultValue: ['option1', 'option2'],
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value: 'Option 3',
      },
    ],
    multiple: true,
    readonly: true,
    columns: 2,
    hint: 'Select one or more options',
  },
];

export const DemoCheckboxFields: DemoFieldData<CipoCheckboxConfig, boolean>[] = [
  {
    type: CipoFieldTypes.Checkbox,
    name: '1basicCheckbox',
    label: 'Basic checkbox',
    defaultValue: true,
    hint: 'checkbox hint',
  },
  {
    type: CipoFieldTypes.Checkbox,
    label: 'Required checkbox',
    name: '2requiredCheckbox',
    required: true,
    fieldDescription: 'This is a required checkbox',
  },
  {
    type: CipoFieldTypes.Checkbox,
    label: 'Readonly positive checkbox',
    name: '3disabledCheckbox',
    defaultValue: true,
    readonly: true,
    hint: 'required checkbox, default checked',
  },
  {
    type: CipoFieldTypes.Checkbox,
    label: 'Readonly negative checkbox',
    name: '4disabledCheckbox',
    defaultValue: false,
    readonly: true,
    hint: 'required checkbox, default not checked',
  },
  {
    type: CipoFieldTypes.Checkbox,
    label:
      'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
    name: '5longCheckbox',
    defaultValue: true,
    hint: 'checkbox hint',
  },
  {
    type: CipoFieldTypes.Checkbox,
    label:
      'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
    name: '6longCheckbox',
    fieldDescription: 'This is a checkbox with long text',
    defaultValue: true,
    hint: 'checkbox hint',
  },
];

export const DemoFieldsWithAttachment: DemoFieldData<CipoAttachmentConfig, AttachmentModel[] | AttachmentModel>[] = [
  {
    type: CipoFieldTypes.Attachment,
    name: '1attachment',
    label: 'Attachment',
    hint: 'Upload your file',
    multiple: true,
    defaultValue: [
      {
        id: 1,
        version: 1,
        versionId: 1,
        mimeType: MimeTypesEnum.XLSX,
        mimeTypeId: 1,
        parentId: 0,
        length: 12345,
        name: 'demo excel',
        order: 1,
      },
    ],
  },
  {
    type: CipoFieldTypes.Attachment,
    name: '2attachment',
    label: 'Attachment with description',
    hint: 'Upload your file',
    required: true,
    fieldDescription: 'This is an attachment field',
    defaultValue: {
      id: 1,
      version: 1,
      versionId: 1,
      mimeType: MimeTypesEnum.DOCX,
      mimeTypeId: 1,
      parentId: 0,
      length: 12345,
      name: 'demo word',
      order: 1,
    },
  },
  {
    type: CipoFieldTypes.Attachment,
    name: '3attachment',
    multiple: true,
    label: 'Attachment with multiple save',
    hint: 'And one file with big name',
    defaultValue: [
      {
        id: 1,
        version: 1,
        versionId: 1,
        mimeType: MimeTypesEnum.PNG,
        mimeTypeId: 1,
        parentId: 0,
        length: 12345,
        name: 'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
        order: 1,
        thumbnail50:
          '/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAcFBQYFBAcGBQYIBwcIChELCgkJChUPEAwRGBUaGRgVGBcbHichGx0lHRcYIi4iJSgpKywrGiAvMy8qMicqKyr/2wBDAQcICAoJChQLCxQqHBgcKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKir/wAARCAAyADIDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD6AgvLaGII14ZiP45MFj+QFSf2ja/890/KozM5xi/I9f3HWrK3kIUBpCxA5Ow8/pT0DUi/tG1/57p+VH9o2v8Az3T8qm+2wf3z/wB8n/Cj7bB/fP8A3yf8KNA1If7Rtf8Anun5Uf2ja/8APdPyqb7bB/fP/fJ/wo+2wf3z/wB8n/CjQNSH+0bX/nun5UVN9tg/vn/vk/4UUaBqMjglSJVZ2kZRgu2Mt7nHFO8p/T9adawRQIVgZmGeS0hc5x6k1NSAr+U/p+tHlP6frViigCv5T+n60eU/p+tWKKAK/lP6frRViigCvZPaNBiw8vylwB5QwvT29qsVRt9YsbhCyzrGPSQhSePQ1N/aNl/z9wf9/BQBYoqv/aNl/wA/cH/fwUf2jZf8/cH/AH8FAFiiq/8AaNl/z9wf9/BR/aNl/wA/cH/fwUAWKKr/ANo2X/P3B/38FFADfKj/ALi/lR5Uf9xfyoooAPKj/uL+VHlR/wBxfyoooAPKj/uL+VHlR/3F/KiigA8qP+4v5UUUUAf/2Q==',
      },
      {
        id: 1,
        version: 1,
        versionId: 1,
        mimeType: MimeTypesEnum.XLSX,
        mimeTypeId: 1,
        parentId: 0,
        length: 12345,
        name: 'excel xlsx',
        order: 1,
        thumbnail50:
          '/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAcFBQYFBAcGBQYIBwcIChELCgkJChUPEAwRGBUaGRgVGBcbHichGx0lHRcYIi4iJSgpKywrGiAvMy8qMicqKyr/2wBDAQcICAoJChQLCxQqHBgcKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKir/wAARCAAyADIDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD6AgvLaGII14ZiP45MFj+QFSf2ja/890/KozM5xi/I9f3HWrK3kIUBpCxA5Ow8/pT0DUi/tG1/57p+VH9o2v8Az3T8qm+2wf3z/wB8n/Cj7bB/fP8A3yf8KNA1If7Rtf8Anun5Uf2ja/8APdPyqb7bB/fP/fJ/wo+2wf3z/wB8n/CjQNSH+0bX/nun5UVN9tg/vn/vk/4UUaBqMjglSJVZ2kZRgu2Mt7nHFO8p/T9adawRQIVgZmGeS0hc5x6k1NSAr+U/p+tHlP6frViigCv5T+n60eU/p+tWKKAK/lP6frRViigCvZPaNBiw8vylwB5QwvT29qsVRt9YsbhCyzrGPSQhSePQ1N/aNl/z9wf9/BQBYoqv/aNl/wA/cH/fwUf2jZf8/cH/AH8FAFiiq/8AaNl/z9wf9/BR/aNl/wA/cH/fwUAWKKr/ANo2X/P3B/38FFADfKj/ALi/lR5Uf9xfyoooAPKj/uL+VHlR/wBxfyoooAPKj/uL+VHlR/3F/KiigA8qP+4v5UUUUAf/2Q==',
      },
    ],
    readonly: true,
  },
  {
    type: CipoFieldTypes.Attachment,
    name: '4attachment',
    label: 'Attachment with thumbnail',
    hint: 'Upload your file',
    defaultValue: {
      id: 169968,
      version: 1,
      versionId: 185079,
      mimeType: MimeTypesEnum.EMAIL,
      mimeTypeId: 1,
      parentId: 0,
      length: 12345,
      name: 'image jpeg, no thumbnail',
      order: 1,
    },
  },
];

export const DemoAttachments: AttachmentModel[] = [
  {
    id: 1,
    version: 1,
    versionId: 1,
    mimeType: MimeTypesEnum.XLSX,
    mimeTypeId: 1,
    parentId: 0,
    length: 12345,
    name: 'demo excel',
    order: 1,
    highlighted: true,
  },
  {
    id: 2,
    version: 2,
    versionId: 2,
    mimeType: MimeTypesEnum.DOCX,
    mimeTypeId: 1,
    parentId: 0,
    length: 12345,
    name: 'demo word',
    order: 1,
    disabled: true,
  },
  {
    id: 3,
    version: 3,
    versionId: 3,
    mimeType: MimeTypesEnum.PNG,
    mimeTypeId: 1,
    parentId: 0,
    length: 12345,
    name: 'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
    order: 1,
    thumbnail50:
      '/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAcFBQYFBAcGBQYIBwcIChELCgkJChUPEAwRGBUaGRgVGBcbHichGx0lHRcYIi4iJSgpKywrGiAvMy8qMicqKyr/2wBDAQcICAoJChQLCxQqHBgcKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKir/wAARCAAyADIDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD6AgvLaGII14ZiP45MFj+QFSf2ja/890/KozM5xi/I9f3HWrK3kIUBpCxA5Ow8/pT0DUi/tG1/57p+VH9o2v8Az3T8qm+2wf3z/wB8n/Cj7bB/fP8A3yf8KNA1If7Rtf8Anun5Uf2ja/8APdPyqb7bB/fP/fJ/wo+2wf3z/wB8n/CjQNSH+0bX/nun5UVN9tg/vn/vk/4UUaBqMjglSJVZ2kZRgu2Mt7nHFO8p/T9adawRQIVgZmGeS0hc5x6k1NSAr+U/p+tHlP6frViigCv5T+n60eU/p+tWKKAK/lP6frRViigCvZPaNBiw8vylwB5QwvT29qsVRt9YsbhCyzrGPSQhSePQ1N/aNl/z9wf9/BQBYoqv/aNl/wA/cH/fwUf2jZf8/cH/AH8FAFiiq/8AaNl/z9wf9/BR/aNl/wA/cH/fwUAWKKr/ANo2X/P3B/38FFADfKj/ALi/lR5Uf9xfyoooAPKj/uL+VHlR/wBxfyoooAPKj/uL+VHlR/3F/KiigA8qP+4v5UUUUAf/2Q==',
  },
  {
    id: 4,
    version: 4,
    versionId: 4,
    mimeType: MimeTypesEnum.PNG,
    mimeTypeId: 1,
    parentId: 0,
    length: 12345,
    name: 'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
    order: 1,
  },
];

export const DemoSelectFields: DemoFieldData<CipoListConfig, string | string[]>[] = [
  {
    type: CipoFieldTypes.Select,
    name: '1options',
    label: 'Single select (required)',
    defaultValue: 'option1',
    dataSourceInfo: {
      id: -25,
    },
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
    ],
    required: true,
    hint: 'Select one option',
    fieldDescription: 'This gets the options from a data source',
  },
  {
    type: CipoFieldTypes.Select,
    name: '2options',
    label: 'Multiple select',
    defaultValue: ['option1', 'option2'],
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value:
          'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum',
      },
    ],
    columns: 2,
    multiple: true,
    fieldDescription: 'One long option',
    hint: 'Select one or more options',
  },
  {
    type: CipoFieldTypes.Select,
    name: '3options',
    label: 'Multiple select readonly',
    defaultValue: ['option1', 'option2'],
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value: 'Option 3',
      },
    ],
    multiple: true,
    readonly: true,
    columns: 2,
    hint: 'Select one or more options',
  },
];

export const DemoFieldsWithReadonlyAddress: DemoFieldData<CipoAddressConfig, AddressModel[]>[] = [
  {
    type: CipoFieldTypes.Address,
    name: '01address',
    label: 'Single address readonly, with value, not required',
    hint: 'Set your address here (hint)',
    multiple: false,
    readonly: true,
    required: false,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: '',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '02address',
    label: 'Single address readonly, with value, required',
    multiple: false,
    readonly: true,
    required: true,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '03address',
    label: 'Single address readonly, no value, not required',
    multiple: false,
    readonly: true,
    required: false,
  },
  {
    type: CipoFieldTypes.Address,
    name: '04address',
    label: 'Single address readonly, no value, required',
    multiple: false,
    readonly: true,
    required: true,
  },
  {
    type: CipoFieldTypes.Address,
    name: '08address',
    label: 'Multiple address readonly, with value, not required',
    multiple: true,
    readonly: true,
    required: false,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
      {
        locationId: 2,
        address: 'Bucuresti',
        locationName: 'Office',
        lat: 44.426767,
        lon: 26.102538,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '09address',
    label: 'Multiple address readonly, no value, not required',
    multiple: true,
    readonly: true,
    required: false,
  },
  {
    type: CipoFieldTypes.Address,
    name: '10address',
    label: 'Multiple address readonly, no value, required',
    multiple: true,
    readonly: true,
    required: true,
  },
  {
    type: CipoFieldTypes.Address,
    name: '11address',
    label: 'Single address readonly, location name only, with value',
    multiple: false,
    readonly: true,
    required: false,
    displayType: DisplayType.AddressLocationName,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '12address',
    label: 'Single address readonly, address only, with value',
    multiple: false,
    readonly: true,
    required: false,
    displayType: DisplayType.AddressOnly,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
];

export const DemoFieldsWithEditableAddress: DemoFieldData<CipoAddressConfig, AddressModel[]>[] = [
  {
    type: CipoFieldTypes.Address,
    name: '05address',
    label: 'Single address editable, no value, required',
    multiple: false,
    readonly: false,
    required: true,
  },
  {
    type: CipoFieldTypes.Address,
    name: '06address',
    label: 'Single address editable, no value, not required',
    multiple: false,
    readonly: false,
    required: false,
  },
  {
    type: CipoFieldTypes.Address,
    name: '07address',
    label: 'Single address editable, with value, required',
    multiple: false,
    readonly: false,
    required: true,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '08address',
    label: 'Single address editable, with value, not required',
    multiple: false,
    readonly: false,
    required: false,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '11address',
    label: 'Multiple address editable, no value, required',
    multiple: true,
    readonly: false,
    required: true,
  },
  {
    type: CipoFieldTypes.Address,
    name: '12address',
    label: 'Multiple address editable, no value, not required',
    multiple: true,
    readonly: false,
    required: false,
  },
  {
    type: CipoFieldTypes.Address,
    name: '13address',
    label: 'Multiple address editable, with value, not required',
    multiple: true,
    readonly: false,
    required: false,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '14address',
    label: 'Multiple address editable, with value, required',
    multiple: true,
    readonly: false,
    required: true,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
      {
        locationId: 2,
        address: 'Bucuresti',
        locationName: 'Office',
        lat: 44.426767,
        lon: 26.102538,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '15address',
    label: 'Single address editable, location name only, with value',
    multiple: false,
    readonly: false,
    required: false,
    displayType: DisplayType.AddressLocationName,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '16address',
    label: 'Single address editable, address only, with value',
    multiple: false,
    readonly: false,
    required: false,
    displayType: DisplayType.AddressOnly,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
];

export const DemoFieldsWithAutocompleteAddress: DemoFieldData<CipoAddressConfig, AddressModel[]>[] = [
  {
    type: CipoFieldTypes.Address,
    name: '01address-autocomplete',
    label: 'Single address autocomplete, not required',
    displayType: DisplayType.AddressAutocomplete,
    required: false,
    defaultValue: [],
  },
  {
    type: CipoFieldTypes.Address,
    name: '02address-autocomplete',
    label: 'Single address autocomplete, required',
    displayType: DisplayType.AddressAutocomplete,
    required: true,
    defaultValue: [],
  },
  {
    type: CipoFieldTypes.Address,
    name: '03address-autocomplete',
    label: 'Single address autocomplete, with pre-filled value',
    displayType: DisplayType.AddressAutocomplete,
    required: false,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '04address-autocomplete',
    label: 'Single address autocomplete, with hint',
    displayType: DisplayType.AddressAutocomplete,
    hint: 'Start typing to search for an address',
    required: false,
    defaultValue: [],
  },
  {
    type: CipoFieldTypes.Address,
    name: '05address-autocomplete',
    label: 'Multiple address autocomplete, not required',
    displayType: DisplayType.AddressAutocomplete,
    multiple: true,
    required: false,
    defaultValue: [],
  },
  {
    type: CipoFieldTypes.Address,
    name: '06address-autocomplete',
    label: 'Multiple address autocomplete, required',
    displayType: DisplayType.AddressAutocomplete,
    multiple: true,
    required: true,
    defaultValue: [],
  },
  {
    type: CipoFieldTypes.Address,
    name: '07address-autocomplete',
    label: 'Multiple address autocomplete, with pre-filled values',
    displayType: DisplayType.AddressAutocomplete,
    multiple: true,
    required: false,
    defaultValue: [
      {
        locationId: 1,
        address: 'Timisoara',
        locationName: 'Home',
        lat: 45.7465,
        lon: 21.2238,
      },
      {
        locationId: 2,
        address: 'Bucharest',
        locationName: 'Office',
        lat: 44.4268,
        lon: 26.1025,
      },
    ],
  },
  {
    type: CipoFieldTypes.Address,
    name: '08address-autocomplete',
    label: 'Multiple address autocomplete, with hint',
    displayType: DisplayType.AddressAutocomplete,
    multiple: true,
    hint: 'Start typing to add multiple addresses',
    required: false,
    defaultValue: [],
  },
];

/* */
export const DemoReadonlyFields: {
  value?: BaseFieldValueType;
  config: CipoFieldConfig;
  readonlyConfig?: CipoTextViewConfig;
}[] = [
  // Text fields
  {
    value: 'John Doe',
    config: {
      type: CipoFieldTypes.Text,
      label: 'Text Field',
    },
  },
  {
    config: {
      type: CipoFieldTypes.Text,
      label: 'Text Field history',
    },
    readonlyConfig: {
      newValue: 'John Doe',
      oldValue: 'Jane Doe',
    },
  },
  {
    value: ['John Doe', 'Jane Doe'],
    config: {
      type: CipoFieldTypes.Text,
      multiple: true,
      label: 'Multi Text Field',
    },
  },
  {
    value: ['John Doe', 'Jane Doe'],
    config: {
      type: CipoFieldTypes.Text,
      multiple: true,
      label: 'Multi Text Field history',
    },
    readonlyConfig: {
      newValue: ['Will Smith'],
      oldValue: ['Jane Dow', 'John Dow'],
    },
  },

  //   Number fields
  {
    value: 12345.67,
    config: {
      type: CipoFieldTypes.Number,
      label: 'Number Field',
    },
  },
  {
    config: {
      type: CipoFieldTypes.Number,
      label: 'Number Field history',
    },
    readonlyConfig: {
      newValue: 12345.67,
      oldValue: 23456.78,
    },
  },
  {
    value: [12345.67, 23456.78],
    config: {
      type: CipoFieldTypes.Number,
      label: 'Multi Number Field',
      multiple: true,
    },
  },
  {
    value: [12345.67, 23456.78],
    config: {
      type: CipoFieldTypes.Number,
      label: 'Multi Number Field history',
      multiple: true,
    },
    readonlyConfig: {
      newValue: [12345.67, 23456.78],
      oldValue: [34567.89, 45678.9],
    },
  },
  //   Date Time fields
  {
    value: '2023-10-01T12:00:00Z',
    config: {
      type: CipoFieldTypes.DateTime,
      label: 'Date Time Field',
    },
  },
  {
    config: {
      type: CipoFieldTypes.DateTime,
      label: 'Date Time Field history',
    },
    readonlyConfig: {
      newValue: '2023-10-01T12:00:00Z',
      oldValue: '2023-09-01T12:00:00Z',
    },
  },
  {
    value: ['2023-10-01T12:00:00Z', '2023-10-02T12:00:00Z'],
    config: {
      type: CipoFieldTypes.DateTime,
      label: 'Date Time Range Field',
      selectMode: 'range',
    },
  },
  {
    config: {
      type: CipoFieldTypes.DateTime,
      selectMode: 'range',
      label: 'Date Time Range Field history',
    },
    readonlyConfig: {
      newValue: ['2023-10-03T12:00:00Z', '2023-10-04T12:00:00Z'],
      oldValue: ['2023-09-01T12:00:00Z', '2023-09-02T12:00:00Z'],
    },
  },
  //   Listbox fields
  {
    value: ['option1', 'option2'],
    config: {
      type: CipoFieldTypes.Listbox,
      label: 'Listbox Field',
    },
  },
  {
    value: ['option10'],
    config: {
      type: CipoFieldTypes.Listbox,
      label: 'Listbox Field history',
    },
    readonlyConfig: {
      newValue: ['option3', 'option4'],
      oldValue: ['option1', 'option2'],
    },
  },
  //   Checkbox fields
  {
    value: true,
    config: {
      type: CipoFieldTypes.Checkbox,
      label: 'Checkbox Field',
    },
  },
  {
    config: {
      type: CipoFieldTypes.Checkbox,
      label: 'Checkbox Field history',
    },
    readonlyConfig: {
      newValue: true,
    },
  },
  //   Rich Text fields
  {
    value: '<p>This is a <strong>rich text</strong> field with <em>some</em> formatting.</p>',
    config: {
      type: CipoFieldTypes.RichText,
      label: 'Rich Text Field',
    },
  },
  {
    config: {
      type: CipoFieldTypes.RichText,
      label: 'Rich Text Field history',
    },
    readonlyConfig: {
      oldValue:
        '<p class="cipo-bg--warn-100">Old content with <strong>bold</strong> text and <em>italic</em> text.</p>',
      newValue:
        '<p class="cipo-bg--accent-100">New content with <strong>bold</strong> text and <em>italic</em> text, plus a link to <a href="https://example.com',
    },
  },
  //   Attachment fields
  {
    value: getAttachments(),
    config: {
      type: CipoFieldTypes.Attachment,
      label: 'Attachment Field',
      readonly: true,
      multiple: true,
    },
  },
  {
    value: getAttachments(),
    config: {
      type: CipoFieldTypes.Attachment,
      label: 'Attachment Field',
      readonly: true,
      multiple: true,
    },
    readonlyConfig: {
      newValue: getAttachments().map(attachment => ({ ...attachment, bgColor: ACCENT_BG_COLOR })),
      oldValue: getAttachments().map(attachment => ({ ...attachment, bgColor: WARN_BG_COLOR })),
    },
  },
];

function getAttachments(): AttachmentModel[] {
  const attachments: AttachemntFieldValueType[] = [
    {
      id: 169968,
      versionId: 185079,
      version: 0,
      name: 'Screenshot 2024-06-05 at 17.00.39.png',
      mimeTypeId: 3,
      mimeType: MimeTypesEnum.PNG,
      parentId: 0,
      thumbnail50:
        '/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAcFBQYFBAcGBQYIBwcIChELCgkJChUPEAwRGBUaGRgVGBcbHichGx0lHRcYIi4iJSgpKywrGiAvMy8qMicqKyr/2wBDAQcICAoJChQLCxQqHBgcKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKioqKir/wAARCAAyADIDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwDyBprnYNs9/ubld2cH9ajE+oswVJboljwAzZNKZpysQ+zMMLhTtPzcUgu5IWJaDB27V3Z+X396OodBBcagcYlueenzNzTDe3inDXM4PvIaPtZCgBB0x17VASWYk9TSGT/b7v8A5+p/+/ho+33n/P3P/wB/T/jVeimIsfb7z/n7n/7+H/Giq9FAEn26T5f3g+UYHyD/AAqN5/M++2fwrVktEwdtrGePvBDimrZoiAywxKM4LOp59elAXMrzF9aPMX1rWeC38omGCMybvlR0zkY9u9UnbypMPawKV4IKH/GnZoV7lbzF9aPMX1qcSqCf9HgOT0KdP1oaZWUj7PAOOoQ8frSGQeYvrRU3mr/zwh/75opgLNbSxrmYcbsfeB5qHaPStQ3MEqsZQ2GP3WBPtnpjPemBbF1w4cbTwQh5Hc9KLCuZ20elGBWgYbAkjdMoz1Kt/wDE0kcdiw+fzkO7GME8evAosFyjgUYFXmhsSo2tKh75ViP5VAkMLKC9wUOTlfKY4Hr70WC5BgUVrx2mk+WvmagA2BuH2aTg0UWGdD5Sf3F/L/PrR5af3F/KiimSGxTjKqfwpDGmfuL+VFFAgKKVwVBHpimi3hB3CGMH1CiiilIqIn2WA8mCMn/cFFFFSUf/2Q==',
      createdOn: '0001-01-01T00:00:00',
      updatedOn: '0001-01-01T00:00:00',
      isFolder: false,
      length: 419458,
      setCompanyLogo: false,
      setUserLogo: false,
      order: 1,
      folderPosition: 1,
      isValid: true,
      toDownload: false,
      showMultipleSave: true,
      projectFilesName: 'Project Files',
      myDriveName: 'My Drive',
      note: 'test',
      currentCulture: 0,
      currentCultureIgnoreCase: 1,
      invariantCulture: 2,
      invariantCultureIgnoreCase: 3,
      ordinal: 4,
      ordinalIgnoreCase: 5,
    },
    {
      id: 170616,
      versionId: 185691,
      version: 0,
      name: 'Book1.xlsx',
      mimeTypeId: 21,
      mimeType: MimeTypesEnum.XLSX,
      parentId: 0,
      createdOn: '0001-01-01T00:00:00',
      updatedOn: '0001-01-01T00:00:00',
      isFolder: false,
      length: 8835,
      setCompanyLogo: false,
      setUserLogo: false,
      order: 2,
      folderPosition: 1,
      isValid: true,
      toDownload: false,
      showMultipleSave: true,
      projectFilesName: 'Project Files',
      myDriveName: 'My Drive',
      currentCulture: 0,
      currentCultureIgnoreCase: 1,
      invariantCulture: 2,
      invariantCultureIgnoreCase: 3,
      ordinal: 4,
      ordinalIgnoreCase: 5,
    },
  ];
  return attachments.map(
    ({ id, name, parentId, version, versionId, mimeType, mimeTypeId, length, order, thumbnail50, bgColor }) => ({
      id,
      name,
      parentId,
      version,
      versionId,
      mimeType,
      mimeTypeId,
      length,
      order,
      thumbnail50,
    }),
  );
}

export const DemoAutocompleteFields: DemoFieldData<CipoListConfig, string[]>[] = [
  {
    type: CipoFieldTypes.Listbox,
    name: 'autocomplete',
    label: 'Autocomplete',
    hint: 'Select one or more options (required)',
    required: true,
    defaultValue: [],
    dataSourceInfo: {
      id: -25,
    },
  },
  {
    type: CipoFieldTypes.Listbox,
    name: 'autocompleteNew',
    allowNewValues: true,
    label: 'Autocomplete with new',
    defaultValue: ['option1', 'some random value'],
    hint: 'Select one or more options or add a new one',
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value: 'Option 3',
      },
      {
        key: 'option4',
        value: 'Option 4',
      },
      {
        key: 'option5',
        value: 'Option 5',
      },
    ],
  },
  {
    type: CipoFieldTypes.Listbox,
    name: 'autocompleteReadonly',
    allowNewValues: true,
    label: 'Autocomplete with new',
    readonly: true,
    defaultValue: ['option1', 'option2'],
    hint: 'Select one or more options or add a new one',
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value: 'Option 3',
      },
      {
        key: 'option4',
        value: 'Option 4',
      },
      {
        key: 'option5',
        value: 'Option 5',
      },
    ],
  },
  {
    type: CipoFieldTypes.Listbox,
    name: 'autocompleteDragAndDrop',
    allowNewValues: true,
    label: 'Autocomplete with Drag and Drop',
    allowDragAndDrop: true,
    defaultValue: ['option1', 'option2'],
    hint: 'Select one or more options or add a new one',
    options: [
      {
        key: 'option1',
        value: 'Option 1',
      },
      {
        key: 'option2',
        value: 'Option 2',
      },
      {
        key: 'option3',
        value: 'Option 3',
      },
      {
        key: 'option4',
        value: 'Option 4',
      },
      {
        key: 'option5',
        value: 'Option 5',
      },
    ],
  },
];
export const DemoRadioGroupFields: DemoFieldData<CipoListConfig, string | string[]>[] = [
  {
    type: CipoFieldTypes.RadioGroup,
    name: '1options',
    label: 'Single Radio (required)',
    defaultValue: 'option1',
    options: [
      { key: 'option1', value: 'Option 1' },
      { key: 'option2', value: 'Option 2' },
      { key: 'option3', value: 'Option 3' },
    ],
    required: true,
    hint: 'Select one option',
  },
  {
    type: CipoFieldTypes.RadioGroup,
    name: '2options',
    label: 'Multiple Checkbox',
    defaultValue: ['option1', 'option2'],
    options: [
      { key: 'option1', value: 'Option 1' },
      { key: 'option2', value: 'Option 2' },
      { key: 'option3', value: 'Option 3' },
    ],
    multiple: true,
    hint: 'Select one or more options',
  },
  {
    type: CipoFieldTypes.RadioGroup,
    name: '3options',
    label: 'Readonly Single',
    defaultValue: 'option2',
    options: [
      { key: 'option1', value: 'Option 1' },
      { key: 'option2', value: 'Option 2' },
      { key: 'option3', value: 'Option 3' },
    ],
    readonly: true,
    hint: 'Readonly mode',
  },
  {
    type: CipoFieldTypes.RadioGroup,
    name: '4options',
    label: 'Readonly Multiple',
    defaultValue: ['option1', 'option3'],
    options: [
      { key: 'option1', value: 'Option 1' },
      { key: 'option2', value: 'Option 2' },
      { key: 'option3', value: 'Option 3' },
    ],
    multiple: true,
    readonly: true,
  },
  {
    type: CipoFieldTypes.RadioGroup,
    name: '5options',
    label: 'Max Height with Rows',
    defaultValue: [],
    options: Array.from({ length: 20 }, (_, i) => ({
      key: `opt${i}`,
      value: `Option ${i} with a somewhat long text to verify wrapping`,
    })),
    multiple: true,
    position: {
      x: 0,
      y: 0,
      cols: 1,
      rows: 2, // Should limit height
    },
    hint: 'Should act as scrollable container',
  },
  {
    type: CipoFieldTypes.RadioGroup,
    name: '6testRadio',
    label: 'test radio',
    defaultValue: ['2', '8'],
    options: [
      { key: '2', value: '30% Design' },
      { key: '12', value: '60% Design' },
      { key: '13', value: '90% Design' },
      { key: '8', value: 'Cost Estimate' },
      { key: '5', value: 'DFLP' },
      { key: '14', value: 'EX-1 EXISTING CONDITIONS-1' },
      { key: '10', value: 'Final Design Submittal' },
      { key: '9', value: 'Other' },
      { key: '1', value: 'Pre Design Report' },
      { key: '7', value: 'Survey/Utility Research' },
      { key: '6', value: 'Technical Memoranda' },
    ],
    multiple: true,
    columns: 2, // Display in 2 columns
    position: {
      x: 0,
      y: 3,
      cols: 6,
      rows: 1,
    },
    hint: '2 columns',
  },
];
